/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

export const STUDIO_INDICATOR_STATE_STORAGE_KEY = 'studio:active-indicators:v1';

const MAX_INDICATORS = 50;
const MAX_SPECS = 50;

const cloneJsonValue = (value, fallback) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return fallback;
  }
};

const sortObject = (value) => {
  if (Array.isArray(value)) return value.map(sortObject);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, sortObject(value[key])]),
  );
};

export const getStudioIndicatorDefinitionKey = (indicator) => JSON.stringify([
  String(indicator?.name || '').toLowerCase(),
  sortObject(indicator?.params || {}),
  String(indicator?.paneType || indicator?.pane_type || ''),
]);

export const normalizeStudioIndicatorDefinitions = (indicators) => {
  const seen = new Set();
  const definitions = [];

  (Array.isArray(indicators) ? indicators : []).forEach((indicator) => {
    const name = String(indicator?.name || '').trim();
    if (!name) return;

    const definition = {
      name,
      params: cloneJsonValue(indicator?.params || {}, {}),
      paneType: indicator?.paneType || indicator?.pane_type || undefined,
      scaleType: indicator?.scaleType || indicator?.scale_type || undefined,
    };
    if (indicator?.visual_overrides && typeof indicator.visual_overrides === 'object') {
      definition.visual_overrides = cloneJsonValue(indicator.visual_overrides, {});
    }

    const key = getStudioIndicatorDefinitionKey(definition);
    if (seen.has(key)) return;
    seen.add(key);
    definitions.push(definition);
  });

  return definitions.slice(0, MAX_INDICATORS);
};

const normalizeSpecDefinitions = (specSubscriptions) => {
  const entries = specSubscriptions instanceof Map
    ? Array.from(specSubscriptions.entries()).map(([uniqueId, meta]) => ({ uniqueId, ...meta }))
    : (Array.isArray(specSubscriptions) ? specSubscriptions : []);
  const seen = new Set();

  return entries.reduce((definitions, entry) => {
    const uniqueId = String(entry?.uniqueId || '').trim();
    if (!uniqueId || !entry?.spec || seen.has(uniqueId)) return definitions;
    seen.add(uniqueId);

    const attachToMeta = entry.attachToMeta && typeof entry.attachToMeta === 'object'
      ? {
          parentName: entry.attachToMeta.parentName,
          parentParams: cloneJsonValue(entry.attachToMeta.parentParams || {}, {}),
        }
      : undefined;
    definitions.push({
      uniqueId,
      spec: cloneJsonValue(entry.spec, null),
      count: Number.isFinite(Number(entry.count)) ? Number(entry.count) : 500,
      ...(attachToMeta?.parentName ? { attachToMeta } : {}),
    });
    return definitions;
  }, []).slice(0, MAX_SPECS);
};

export const serializeStudioIndicatorState = (indicators, specSubscriptions) => JSON.stringify({
  version: 1,
  indicators: normalizeStudioIndicatorDefinitions(indicators),
  specs: normalizeSpecDefinitions(specSubscriptions),
});

export const loadStudioIndicatorState = () => {
  try {
    const raw = window.localStorage.getItem(STUDIO_INDICATOR_STATE_STORAGE_KEY);
    if (!raw) return { indicators: [], specs: [] };
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1) return { indicators: [], specs: [] };
    return {
      indicators: normalizeStudioIndicatorDefinitions(parsed.indicators),
      specs: normalizeSpecDefinitions(parsed.specs),
    };
  } catch {
    return { indicators: [], specs: [] };
  }
};

export const storeStudioIndicatorState = (serializedState) => {
  try {
    window.localStorage.setItem(STUDIO_INDICATOR_STATE_STORAGE_KEY, serializedState);
  } catch {
    // Persistence is best-effort in locked-down browser contexts.
  }
};
